// CtaBlockEditor — call-to-action button.

import type { CtaBlock, Block, Align } from "../../../lib/blocks";
import { t } from "../theme";

export interface CtaBlockEditorProps {
  block: CtaBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
}

const STYLES: CtaBlock["style"][] = ["primary", "secondary", "subtle"];

export function CtaBlockEditor({
  block,
  onChange,
  isSelected,
}: CtaBlockEditorProps) {
  const style = block.style || "primary";
  const align = block.align || "center";

  return (
    <div>
      {isSelected && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, fontFamily: t.sans, flexWrap: "wrap" }}>
          {STYLES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onChange({ style: s } as Partial<CtaBlock>)}
              style={pillStyle(style === s)}
            >
              {s}
            </button>
          ))}
          <div style={{ width: 8 }} />
          {(["left", "center", "right"] as Align[]).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => onChange({ align: a } as Partial<CtaBlock>)}
              style={pillStyle(align === a)}
            >
              {a}
            </button>
          ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent:
            align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center",
        }}
      >
        <span
          style={{
            display: "inline-block",
            padding: "10px 20px",
            borderRadius: 6,
            fontFamily: t.sans,
            fontSize: 14,
            fontWeight: 600,
            background:
              style === "primary" ? t.accent : style === "secondary" ? "transparent" : "transparent",
            color:
              style === "primary" ? "#1c1c1e" : t.accent,
            border:
              style === "secondary" ? `1px solid ${t.accent}` : style === "subtle" ? "none" : `1px solid ${t.accent}`,
            textDecoration: style === "subtle" ? "underline" : "none",
          }}
        >
          {block.label || "Button label"}
        </span>
      </div>

      {isSelected && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginTop: 10,
            fontFamily: t.sans,
          }}
        >
          <input
            type="text"
            value={block.label}
            onChange={(e) => onChange({ label: e.target.value } as Partial<CtaBlock>)}
            placeholder="Label"
            style={inputStyle}
          />
          <input
            type="url"
            value={block.url}
            onChange={(e) => onChange({ url: e.target.value } as Partial<CtaBlock>)}
            placeholder="https://…"
            style={inputStyle}
          />
        </div>
      )}
    </div>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    cursor: "pointer",
    textTransform: "capitalize",
  };
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "6px 8px",
  background: t.bg,
  border: `1px solid ${t.border}`,
  borderRadius: 4,
  fontSize: 12,
  color: t.text,
  outline: "none",
  fontFamily: t.sans,
};
